import React, { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { DEFAULT_OBSERVER_OPTIONS } from '../../constants/ovserver';
import { cn } from '../../utils/classname';
import { Title } from '../title/title';
import './circular-progress.scss';
import { useUpdateCurrentValue } from './useUpdateValue';

type CircularProgressProps = {
  value?: number;
  increment?: number;
  valueSuffix?: string;
  filledColor?: string;
  unfilledColor?: string;
  children?: ReactNode;
  animationSpeed?: number;
  enableAnimation?: boolean;
  className?: string;
  animationName: string;
};

const CircularProgress: React.FC<CircularProgressProps> = ({
  value = 0,
  increment = 1,
  valueSuffix = '',
  animationSpeed = 30,
  enableAnimation = true,
  animationName,
  filledColor,
  unfilledColor,
  children,
  className
}) => {
  const strokeWidth = 20;

  const [offset, setOffset] = useState(0);
  const [size, setSize] = useState(240);
  const { ref, inView } = useInView(DEFAULT_OBSERVER_OPTIONS);
  const svgRef = useRef(null);
  const animatedCircleRef = useRef(null);
  const shouldAnimate = inView;

  const { animatedValue } = useUpdateCurrentValue({ shouldAnimate, value, valueSuffix, animationSpeed, increment });

  const center = size / 2;
  const radius = size / 2 - strokeWidth / 2;
  const circumference = 2 * Math.PI * radius;
  const animationDuration = (value * animationSpeed) / 1000;

  const updateOffset = useCallback(() => {
    const progressOffset = ((100 - value) / 100) * circumference;
    setOffset(progressOffset);
  }, [circumference, value]);

  useEffect(() => {
    const updateSize = () => {
      if (svgRef.current) {
        setSize(svgRef.current.getBoundingClientRect().width);
      }
    };

    updateSize();

    window.addEventListener('resize', updateSize);
    return () => {
      window.removeEventListener('resize', updateSize);
    };
  }, []);

  useEffect(() => {
    if (shouldAnimate) {
      updateOffset();
      if (animatedCircleRef.current) {
        animatedCircleRef.current.animate([{ strokeDashoffset: circumference }, { strokeDashoffset: offset }], {
          duration: animationDuration * 1000,
          fill: 'forwards'
        });
      }
    }
  }, [updateOffset, shouldAnimate, circumference, offset, animationDuration]);

  return (
    <div className={cn('rc-circular-progress-bar-container', className)} ref={ref} data-animate={animationName}>
      <svg ref={svgRef} className="rc-circular-progress-bar" width={size} height={size}>
        <circle
          stroke={unfilledColor}
          fill="transparent"
          strokeWidth={strokeWidth}
          r={radius}
          cx={center}
          cy={center}
        />
        <circle
          ref={animatedCircleRef}
          stroke={enableAnimation && filledColor}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={`${circumference} ${circumference}`}
          style={{ strokeDashoffset: -offset }}
          strokeLinecap="round"
          r={radius}
          cx={center}
          cy={center}
          transform={`rotate(-90 ${center} ${center})`}
        />
      </svg>
      <div className="rc-circular-progress-bar-value">
        {children ? (
          children
        ) : (
          <Title level="2" weight="bold" align="center">
            {animatedValue}
          </Title>
        )}
      </div>
    </div>
  );
};

export default CircularProgress;
